"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdminSendNotificationRequest,
  AdminUpdateNotificationTemplateRequest,
  NotificationTypeValue,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useAdminNotificationTemplates() {
  return useQuery({
    queryKey: qk.admin.notificationTemplates,
    queryFn: async () => getSdk().admin.listNotificationTemplates(),
    staleTime: 30_000,
  });
}

export function useAdminNotificationTemplate(type: NotificationTypeValue) {
  return useQuery({
    queryKey: qk.admin.notificationTemplate(type),
    queryFn: async () => getSdk().admin.getNotificationTemplate(type),
  });
}

export function useUpdateAdminNotificationTemplate(
  type: NotificationTypeValue,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUpdateNotificationTemplateRequest) =>
      getSdk().admin.updateNotificationTemplate(type, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.admin.notificationTemplates });
      qc.invalidateQueries({ queryKey: qk.admin.notificationTemplate(type) });
    },
  });
}

export function useResetAdminNotificationTemplate(type: NotificationTypeValue) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getSdk().admin.resetNotificationTemplate(type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.admin.notificationTemplates });
      qc.invalidateQueries({ queryKey: qk.admin.notificationTemplate(type) });
    },
  });
}

export function useSendAdminNotification() {
  return useMutation({
    mutationFn: (body: AdminSendNotificationRequest) =>
      getSdk().admin.sendNotification(body),
  });
}
